

import React, { useState } from "react";
import { Refine } from "@refinedev/core";
import { useNotificationProvider } from "@refinedev/antd";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import { dataProvider, liveProvider } from "../providers";
import routerBindings, {
  CatchAllNavigate,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import { Route, Routes, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Dashboard } from "../pages/dashboard";
import { Profile } from "../pages/Profile";
import LandingPage from "../pages/LandingPage";
import AuthPage from "../pages/auth/AuthPage";
import { VerifyEmail } from "../pages/VerifyEmail";
import { ForgotPassword } from "../pages/ForgotPassword";
import { ResetPassword } from "../pages/ResetPassword";
import { OAuthCallback } from "../pages/auth/OAuthCallback";
import Terms from "../pages/Terms";
import Privacy from "../pages/Privacy";
import Contact from "../pages/Contact";
import SuccessPage from "../pages/SuccessPage";
import CancelPage from "../pages/CancelPage";
import ExpedientPage from "../pages/ExpedientPage";
import ManageSubscriptionPage from "../pages/ManageSubscriptionPage";
import KnowledgeGraphPage from "../pages/knowledge-graph";
import MLDashboard from "../components/MLDashboard";
import ValueDashboard from "../pages/ValueDashboard";
import LocationSetup from "../components/LocationSetup";
import { ErrorBoundary } from "./error-boundary";
import { DataUpload } from "./DataUpload";
import OnboardingFlow from "./OnboardingFlow";
import ContributorDashboard from "../pages/ContributorDashboard";
import DataMarketplace from "../pages/DataMarketplace";
import EnergyDashboard from "../pages/EnergyDashboard";
import CarbonCredits from "../pages/CarbonCredits";
import Conservation from "../pages/Conservation";
import WptFleetDashboard from "../pages/WptFleetDashboard";
import { useLocation } from "../contexts/LocationContext";
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Alert, Button, Card } from "antd";
import {
  DashboardOutlined,
  UserOutlined,
  ThunderboltOutlined,
  BranchesOutlined,
  EnvironmentOutlined,
  LogoutOutlined,
  CrownOutlined,
  RobotOutlined,
  DollarOutlined,
  UploadOutlined,
  RocketOutlined,
  TeamOutlined,
  ShopOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { Logo } from "./Logo";

const { Sider, Content, Header } = Layout;
const { Text } = Typography;

export const AuthenticatedAppContent: React.FC = () => {
  const { user, isLoading, logout } = useAuth();
  const { lat, lng } = useLocation();
  const onboardingCompleted = localStorage.getItem('onboarding_completed') === 'true';

  // Check if user needs onboarding (not subscribed, hasn't completed onboarding)
  const needsOnboarding = user && !user.isSubscribed && !onboardingCompleted && !lat && !lng;

  // Show onboarding badge in menu if not completed and not subscribed
  const showOnboardingBadge = user && !user.isSubscribed && !onboardingCompleted;

  // Check if alert was dismissed this session
  const alertDismissed = sessionStorage.getItem('onboarding_alert_dismissed') === 'true';
  const showOnboardingAlert = showOnboardingBadge && !alertDismissed;

  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  // Avatar dropdown menu
  const avatarMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: <Link to="/profile">Profile Settings</Link>,
    },
    {
      key: "subscription",
      icon: <CrownOutlined />,
      label: <Link to="/manage-subscription">Subscription</Link>,
    },
    {
      type: "divider" as const,
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Logout",
      onClick: logout,
    },
  ];

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Show onboarding for new users
  if (needsOnboarding) {
    return (
      <RefineKbarProvider>
        <Refine
          dataProvider={dataProvider}
          liveProvider={liveProvider}
          notificationProvider={useNotificationProvider}
          routerProvider={routerBindings}
          options={{
            syncWithLocation: true,
            warnWhenUnsavedChanges: true,
            useNewQueryKeys: true,
            projectId: "JDRifN-FX4SzT-De7oEm",
            liveMode: "auto",
          }}
        >
          <Routes>
            <Route path="/onboarding" element={<OnboardingFlow />} />
            <Route path="*" element={<OnboardingFlow />} />
          </Routes>
        </Refine>
      </RefineKbarProvider>
    );
  }

  if (!user) {
    return (
      <RefineKbarProvider>
        <Refine
          dataProvider={dataProvider}
          liveProvider={liveProvider}
          notificationProvider={useNotificationProvider}
          routerProvider={routerBindings}
          options={{
            syncWithLocation: true,
            warnWhenUnsavedChanges: true,
            useNewQueryKeys: true,
            projectId: "JDRifN-FX4SzT-De7oEm",
            liveMode: "auto",
          }}
        >
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/register" element={<AuthPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/auth/callback" element={<OAuthCallback />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/cancel" element={<CancelPage />} />
            <Route path="*" element={<CatchAllNavigate to="/login" />} />
          </Routes>
        </Refine>
      </RefineKbarProvider>
    );
  }

  return (
    <RefineKbarProvider>
      <Refine
        dataProvider={dataProvider}
        liveProvider={liveProvider}
        notificationProvider={useNotificationProvider}
        routerProvider={routerBindings}
        options={{
          syncWithLocation: true,
          warnWhenUnsavedChanges: true,
          useNewQueryKeys: true,
          projectId: "JDRifN-FX4SzT-De7oEm",
          liveMode: "auto",
        }}
      >
        <Layout style={{ minHeight: "100vh", background: "#000000" }}>
          <Sider
            width={240}
            style={{
              background: "#0a0a0a",
              borderRight: "1px solid #1a1a1a",
              position: "sticky",
              top: 0,
              height: "100vh",
              overflow: "auto",
            }}
            breakpoint="lg"
            collapsedWidth="0"
          >
            <div
              style={{
                padding: "24px 16px",
                borderBottom: "1px solid #1a1a1a",
              }}
            >
              <Logo />
              <Text
                style={{
                  color: "#ffffff",
                  fontSize: "18px",
                  fontWeight: 600,
                  marginTop: "12px",
                  display: "block",
                }}
              >
                Expedient
              </Text>
            </div>

            <Menu
              mode="inline"
              defaultSelectedKeys={["dashboard"]}
              style={{
                background: "#0a0a0a",
                borderRight: "none",
                padding: "8px 0",
              }}
              items={[
                {
                  key: "dashboard",
                  icon: <DashboardOutlined />,
                  label: <Link to="/dashboard">Dashboard</Link>,
                },
                {
                  key: "wpt-fleet",
                  icon: <ThunderboltOutlined />,
                  label: <Link to="/wpt-fleet">Dynamic WPT & Fleet</Link>,
                },
                {
                  key: "expedient",
                  icon: <ThunderboltOutlined />,
                  label: <Link to="/expedient">Energy Analysis</Link>,
                },
                {
                  key: "energy-dashboard",
                  icon: <ThunderboltOutlined />,
                  label: <Link to="/energy-dashboard">Energy Intelligence</Link>,
                },
                {
                  key: "carbon-credits",
                  icon: <EnvironmentOutlined />,
                  label: <Link to="/carbon-credits">Carbon Credits</Link>,
                },
                {
                  key: "conservation",
                  icon: <EnvironmentOutlined />,
                  label: <Link to="/conservation">Conservation</Link>,
                },
                {
                  key: "value-dashboard",
                  icon: <DollarOutlined />,
                  label: <Link to="/value-dashboard">Value Dashboard</Link>,
                },
                {
                  key: "data-upload",
                  icon: <UploadOutlined />,
                  label: <Link to="/data-upload">Upload Data</Link>,
                },
                {
                  key: "knowledge-graph",
                  icon: <BranchesOutlined />,
                  label: <Link to="/knowledge-graph">Knowledge Graph</Link>,
                },
                {
                  key: "ml-dashboard",
                  icon: <RobotOutlined />,
                  label: <Link to="/ml-dashboard">ML Analytics</Link>,
                },
                {
                  key: "contributor",
                  icon: <TeamOutlined />,
                  label: <Link to="/contributor">Work for Expedient</Link>,
                },
                {
                  key: "marketplace",
                  icon: <ShopOutlined />,
                  label: <Link to="/marketplace">Data Marketplace</Link>,
                },
                {
                  type: "divider",
                  style: { borderColor: "#1a1a1a", margin: "8px 0" },
                },
                {
                  key: "onboarding",
                  icon: <RocketOutlined />,
                  label: <Link to="/onboarding">Onboarding</Link>,
                },
                {
                  key: "subscription",
                  icon: <CrownOutlined />,
                  label: <Link to="/manage-subscription">Subscription</Link>,
                },
              ]}
            />
          </Sider>

          <Layout>
            <Header
              style={{
                padding: "0 24px",
                background: "#0a0a0a",
                borderBottom: "1px solid #1a1a1a",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                position: "sticky",
                top: 0,
                zIndex: 100,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
                <Button
                  icon={<MenuOutlined />}
                  onClick={() => setMobileMenuVisible(!mobileMenuVisible)}
                  style={{
                    background: "transparent",
                    border: "1px solid #2a2a2a",
                    color: "#ffffff",
                    display: "none",
                  }}
                  className="mobile-menu-button"
                />
              </div>

              <Space>
                <Text style={{ color: "#888888" }}>
                  Welcome back, {user?.name || user?.email}
                </Text>
                <Dropdown
                  menu={{ items: avatarMenuItems }}
                  placement="bottomRight"
                >
                  <Avatar
                    style={{ backgroundColor: "#3b82f6", cursor: "pointer" }}
                    icon={<UserOutlined />}
                    src={user?.avatar}
                  />
                </Dropdown>
              </Space>
            </Header>

            <Content style={{ margin: "24px 16px 0" }}>
              <div
                style={{
                  padding: 24,
                  minHeight: 360,
                  background: "#000000",
                  borderRadius: 4,
                }}
              >
                {showOnboardingAlert && (
                  <Alert
                    message="Complete onboarding to unlock full features"
                    description={
                      <span>
                        Set up your location and data to get personalized insights.
                        <Link to="/onboarding" style={{ marginLeft: '8px', fontWeight: 500 }}>
                          Get Started →
                        </Link>
                      </span>
                    }
                    type="info"
                    showIcon
                    closable
                    style={{ marginBottom: '24px' }}
                    onClose={() => {
                      // Store dismissal for this session
                      sessionStorage.setItem('onboarding_alert_dismissed', 'true');
                    }}
                  />
                )}

                {/* Mobile Menu */}
                {mobileMenuVisible && (
                  <Card
                    style={{
                      position: "fixed",
                      top: "70px",
                      left: "16px",
                      right: "16px",
                      background: "#0a0a0a",
                      border: "1px solid #1a1a1a",
                      zIndex: 99,
                      marginBottom: "16px",
                    }}
                  >
                    <Menu
                      mode="vertical"
                      defaultSelectedKeys={["dashboard"]}
                      items={[
                        {
                          key: "dashboard",
                          icon: <DashboardOutlined />,
                          label: <Link to="/dashboard">Dashboard</Link>,
                        },
                        {
                          key: "wpt-fleet",
                          icon: <ThunderboltOutlined />,
                          label: <Link to="/wpt-fleet">Dynamic WPT & Fleet</Link>,
                        },
                        {
                          key: "expedient",
                          icon: <ThunderboltOutlined />,
                          label: <Link to="/expedient">Energy Analysis</Link>,
                        },
                        {
                          key: "energy-dashboard",
                          icon: <ThunderboltOutlined />,
                          label: <Link to="/energy-dashboard">Energy Intelligence</Link>,
                        },
                        {
                          key: "carbon-credits",
                          icon: <EnvironmentOutlined />,
                          label: <Link to="/carbon-credits">Carbon Credits</Link>,
                        },
                        {
                          key: "conservation",
                          icon: <EnvironmentOutlined />,
                          label: <Link to="/conservation">Conservation</Link>,
                        },
                        {
                          key: "value-dashboard",
                          icon: <DollarOutlined />,
                          label: <Link to="/value-dashboard">Value Dashboard</Link>,
                        },
                        {
                          key: "data-upload",
                          icon: <UploadOutlined />,
                          label: <Link to="/data-upload">Upload Data</Link>,
                        },
                        {
                          key: "knowledge-graph",
                          icon: <BranchesOutlined />,
                          label: <Link to="/knowledge-graph">Knowledge Graph</Link>,
                        },
                        {
                          key: "ml-dashboard",
                          icon: <RobotOutlined />,
                          label: <Link to="/ml-dashboard">ML Analytics</Link>,
                        },
                        {
                          key: "contributor",
                          icon: <TeamOutlined />,
                          label: <Link to="/contributor">Work for Expedient</Link>,
                        },
                        {
                          key: "marketplace",
                          icon: <ShopOutlined />,
                          label: <Link to="/marketplace">Data Marketplace</Link>,
                        },
                        {
                          key: "onboarding",
                          icon: <RocketOutlined />,
                          label: <Link to="/onboarding">Onboarding</Link>,
                        },
                        {
                          key: "subscription",
                          icon: <CrownOutlined />,
                          label: <Link to="/manage-subscription">Subscription</Link>,
                        },
                      ]}
                    />
                  </Card>
                )}

                <Routes>
                  <Route index element={<CatchAllNavigate to="/dashboard" />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ErrorBoundary>
                        <Dashboard />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ErrorBoundary>
                        <Profile />
                      </ErrorBoundary>
                    }
                  />
                  <Route path="/wpt-fleet" element={<WptFleetDashboard />} />
                  <Route path="/expedient" element={<ExpedientPage />} />
                  <Route path="/energy-dashboard" element={<EnergyDashboard />} />
                  <Route path="/carbon-credits" element={<CarbonCredits />} />
                  <Route path="/conservation" element={<Conservation />} />
                  <Route
                    path="/manage-subscription"
                    element={<ManageSubscriptionPage />}
                  />
                  <Route
                    path="/knowledge-graph"
                    element={<KnowledgeGraphPage />}
                  />
                  <Route path="/ml-dashboard" element={<MLDashboard />} />
                  <Route path="/value-dashboard" element={<ValueDashboard />} />
                  <Route path="/data-upload" element={<DataUpload />} />
                  <Route path="/location-setup" element={<LocationSetup />} />
                  <Route path="/onboarding" element={<OnboardingFlow />} />
                  <Route path="/contributor" element={<ContributorDashboard />} />
                  <Route path="/marketplace" element={<DataMarketplace />} />
                  <Route
                    path="*"
                    element={<CatchAllNavigate to="/dashboard" />}
                  />
                </Routes>
              </div>
            </Content>
          </Layout>
        </Layout>
      </Refine>
      <RefineKbar />
      <UnsavedChangesNotifier />
    </RefineKbarProvider>
  );
};
